import { Box, Divider, Input, Text } from '@wix/design-system';
import lodash from 'lodash';
import React, { useContext, useRef } from 'react';

import { EditorContext, ModalServiceContext, RicosContext } from './../../ricos-context';
import { toHexFormat } from './../../ricos-styles';
import { useBiNext } from './../../_shared/bi-context-provider/bi-context-provider';
import type { PmImageNode } from './../../_shared/pm-node-types';
import { BorderColorPicker } from './../components/border-color-picker';
import { ButtonColorSwatches, SliderWithInput } from './../../ui-components';

import { IMAGE_EDITOR_COLOR_PICKER_MODAL_ID, IMAGE_SETTINGS_MODAL_ID } from '../consts';
import {
  SETTINGS_DESIGN_TAB_BORDER_COLOR_PICKER,
  SETTINGS_DESIGN_TAB_BORDER_WIDTH_INPUT,
  SETTINGS_DESIGN_TAB_BORDER_WIDTH_SLIDER,
  SETTINGS_DESIGN_TAB_CORNER_RADIUS_INPUT,
  SETTINGS_DESIGN_TAB_CORNER_RADIUS_SLIDER,
} from '../data-hooks';
import type { ImagePluginEditorConfig } from '../types';

type Props = {
  pmNodeAttrs: PmImageNode['attrs'];
  patchPmNodeAttrs: (data: Partial<PmImageNode['attrs']>) => void;
  nodeId: string;
  config: ImagePluginEditorConfig;
};

export const DesignTab = ({ pmNodeAttrs, patchPmNodeAttrs, nodeId, config }: Props) => {
  const { t, isMobile } = useContext(RicosContext);
  const modalService = useContext(ModalServiceContext) || {};
  const { adapter } = useContext(EditorContext);
  const moreColorsButtonRef = useRef<HTMLDivElement>(null);

  const logBi = useBiNext({ modalId: IMAGE_SETTINGS_MODAL_ID, pluginId: 'image', nodeId });

  const image = nodeId
    ? adapter.tiptapEditor.view.dom.querySelector(`[data-hook=image-viewer-${nodeId}`)
    : undefined;
  const imageStyle = image ? window.getComputedStyle(image) : undefined;
  const defaultBorderColor =
    config.colors?.color8 ?? (imageStyle?.getPropertyValue('--ricos-action-color') || '#116DFF');
  const colorValue = imageStyle?.getPropertyValue('border-color');
  const borderColor = colorValue ? toHexFormat(colorValue) : undefined;

  const borderColorValue =
    pmNodeAttrs.styles?.border.color ?? borderColor ?? defaultBorderColor ?? undefined;

  const borderRadiusValue =
    pmNodeAttrs.styles?.border.radius ??
    parseToInt(imageStyle?.getPropertyValue('border-radius')) ??
    0;

  const borderWidthValue =
    pmNodeAttrs.styles?.border.width ??
    parseToInt(imageStyle?.getPropertyValue('border-width')) ??
    0;

  const openColorPicker = () => {
    const modalId = IMAGE_EDITOR_COLOR_PICKER_MODAL_ID;
    const referenceElement = moreColorsButtonRef.current;

    modalService.togglePluginPanel({
      id: modalId,
      Component: BorderColorPicker,
      componentProps: {
        modalId,
        nodeId,
        referenceElement,
        config,
        defaultColor: borderColorValue,
        onColorSelected: color =>
          patchPmNodeAttrs({
            styles: {
              border: {
                color: color ?? pmNodeAttrs.styles?.border.color ?? null,
                radius: pmNodeAttrs.styles?.border.radius ?? null,
                width: pmNodeAttrs.styles?.border.width ?? null,
              },
            },
          }),
        onColorAdded: color => config.onBorderColorAdded?.(color),
      },
      isMobile,
      positioning: {
        referenceElement,
        placement: 'left',
      },
      type: undefined,
      onClickOutside: lodash.noop,
      focusReturnElement: referenceElement ?? undefined,
    });
  };

  return (
    <Box direction="vertical" gap="SP3" overflow="auto" paddingTop="SP3" paddingBottom="SP3">
      <Box direction="vertical" gap="SP2" paddingLeft="SP3" paddingRight="SP3">
        <Box paddingBottom="SP1">
          <Text size="small" weight="bold">
            {t('ImagePlugin_Design_Corners')}
          </Text>
        </Box>
        <SliderWithInput
          min={0}
          max={30}
          onChange={radius =>
            patchPmNodeAttrs({
              styles: {
                border: {
                  radius,
                  color: pmNodeAttrs.styles?.border.color ?? null,
                  width: pmNodeAttrs.styles?.border.width ?? null,
                },
              },
            })
          }
          logBi={logBi}
          label={t('ImagePlugin_Design_Corner_Radius')}
          sliderDataHook={SETTINGS_DESIGN_TAB_CORNER_RADIUS_SLIDER}
          inputDataHook={SETTINGS_DESIGN_TAB_CORNER_RADIUS_INPUT}
          value={borderRadiusValue}
          suffix={<Input.Affix>{t('Common_Units_PX')}</Input.Affix>}
        />
      </Box>

      <Divider />

      <Box direction="vertical" gap="SP2" paddingLeft="SP3" paddingRight="SP3">
        <Box paddingBottom="SP1">
          <Text size="small" weight="bold">
            {t('ImagePlugin_Design_Border')}
          </Text>
        </Box>

        <Box paddingBottom="SP3">
          <ButtonColorSwatches
            label={t('ImagePlugin_Design_Border_Color')}
            color={borderColorValue}
            onChange={color => {
              logBi({ dataHook: SETTINGS_DESIGN_TAB_BORDER_COLOR_PICKER, value: color });
              patchPmNodeAttrs({
                styles: {
                  border: {
                    color,
                    radius: pmNodeAttrs.styles?.border?.radius ?? null,
                    width: pmNodeAttrs.styles?.border?.width ?? null,
                  },
                },
              });
            }}
            dataHook={SETTINGS_DESIGN_TAB_BORDER_COLOR_PICKER}
            defaultColor={borderColorValue}
            onMorePress={() => {
              logBi({ dataHook: SETTINGS_DESIGN_TAB_BORDER_COLOR_PICKER });
              openColorPicker();
            }}
            moreButtonRef={moreColorsButtonRef}
          />
        </Box>

        <SliderWithInput
          min={0}
          max={15}
          onChange={width => {
            patchPmNodeAttrs({
              styles: {
                border: {
                  width,
                  color: pmNodeAttrs.styles?.border.color ?? null,
                  radius: pmNodeAttrs.styles?.border.radius ?? null,
                },
              },
            });
          }}
          logBi={logBi}
          label={t('ImagePlugin_Design_Border_Width')}
          sliderDataHook={SETTINGS_DESIGN_TAB_BORDER_WIDTH_SLIDER}
          inputDataHook={SETTINGS_DESIGN_TAB_BORDER_WIDTH_INPUT}
          value={borderWidthValue}
          suffix={<Input.Affix>{t('Common_Units_PX')}</Input.Affix>}
        />
      </Box>
    </Box>
  );
};

const parseToInt = (value: string | undefined): number | undefined => {
  if (value === undefined) {
    return undefined;
  }

  const parsedValue = parseInt(value);
  return isNaN(parsedValue) ? undefined : parsedValue;
};
